import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  adminCreateJournal,
  adminGetJournal,
  adminUpdateJournal,
  uploadImage,
} from "../../lib/api";
import { ArrowLeft, ImageIcon, Loader2 } from "lucide-react";

const EMPTY = {
  title: "",
  excerpt: "",
  body: "",
  tag: "News",
  hero_image_id: null,
  published: false,
};

const LABEL_CLS = "block text-xs uppercase tracking-[0.18em] font-accent font-semibold text-ink mb-2";
const INPUT_CLS = "w-full border border-rule bg-linen px-4 py-3 text-base outline-none focus:border-river";

export default function AdminJournalEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const heroInputRef = useRef(null);
  const bodyInputRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminGetJournal(id)
      .then((p) =>
        setForm({
          title: p.title,
          excerpt: p.excerpt,
          body: p.body,
          tag: p.tag,
          hero_image_id: p.hero_image_id,
          published: p.published,
        })
      )
      .catch(() => {
        toast.error("Couldn't load that post.");
        navigate("/admin/journal", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const onField = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSave = async (publish = null) => {
    setSaving(true);
    const payload = { ...form };
    if (publish !== null) payload.published = publish;
    try {
      if (isNew) {
        const created = await adminCreateJournal(payload);
        toast.success(publish ? "Published." : "Draft saved.");
        navigate(`/admin/journal/${created.id}`, { replace: true });
      } else {
        await adminUpdateJournal(id, payload);
        if (publish !== null) setForm((f) => ({ ...f, published: publish }));
        toast.success(publish === true ? "Published." : publish === false ? "Unpublished." : "Saved.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save the post.");
    } finally {
      setSaving(false);
    }
  };

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setForm((f) => ({ ...f, hero_image_id: res.file_id }));
      toast.success("Hero image uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      if (heroInputRef.current) heroInputRef.current.value = "";
    }
  };

  const onInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const md = `\n\n![](/api/media/${res.file_id})\n\n`;
      const ta = bodyInputRef.current;
      if (ta) {
        const start = ta.selectionStart ?? form.body.length;
        const end = ta.selectionEnd ?? form.body.length;
        const next = form.body.slice(0, start) + md + form.body.slice(end);
        setForm((f) => ({ ...f, body: next }));
        // restore caret
        setTimeout(() => {
          ta.focus();
          ta.selectionStart = ta.selectionEnd = start + md.length;
        }, 0);
      } else {
        setForm((f) => ({ ...f, body: f.body + md }));
      }
      toast.success("Image inserted.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  if (loading) return <p className="text-ink-muted font-accent">Loading…</p>;

  return (
    <div data-testid="admin-journal-edit-page">
      <Link
        to="/admin/journal"
        className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink mb-8"
      >
        <ArrowLeft size={14} strokeWidth={1.5} />
        Back to all posts
      </Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl text-ink tracking-tight">
          {isNew ? "New post" : "Edit post"}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="admin-toggle-preview-button"
            onClick={() => setPreview((p) => !p)}
            className="border border-rule text-ink-muted hover:border-ink hover:text-ink px-4 py-2 text-sm font-accent"
          >
            {preview ? "Edit" : "Preview"}
          </button>
          <button
            type="button"
            data-testid="admin-save-draft-button"
            disabled={saving}
            onClick={() => onSave(form.published ? null : false)}
            className="border border-rule text-ink hover:bg-linen-alt px-4 py-2 text-sm font-accent font-semibold disabled:opacity-60"
          >
            Save draft
          </button>
          {form.published ? (
            <button
              type="button"
              data-testid="admin-unpublish-button"
              disabled={saving}
              onClick={() => onSave(false)}
              className="border border-rule text-ink hover:bg-linen-alt px-4 py-2 text-sm font-accent font-semibold disabled:opacity-60"
            >
              Unpublish
            </button>
          ) : (
            <button
              type="button"
              data-testid="admin-publish-button"
              disabled={saving}
              onClick={() => onSave(true)}
              className="bg-river text-linen px-4 py-2 text-sm font-accent font-semibold hover:bg-river-dark disabled:opacity-60"
            >
              Publish
            </button>
          )}
        </div>
      </div>

      {preview ? (
        <PostPreview form={form} />
      ) : (
        <div className="space-y-6 max-w-3xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="title" className={LABEL_CLS}>Title</label>
              <input
                id="title"
                data-testid="admin-post-title-input"
                value={form.title}
                onChange={onField("title")}
                className={INPUT_CLS}
                placeholder="Give it a clear, calm title"
              />
            </div>
            <div>
              <label htmlFor="tag" className={LABEL_CLS}>Tag</label>
              <input
                id="tag"
                data-testid="admin-post-tag-input"
                value={form.tag}
                onChange={onField("tag")}
                className={INPUT_CLS}
                placeholder="News"
              />
            </div>
          </div>

          <div>
            <label htmlFor="excerpt" className={LABEL_CLS}>
              Excerpt
              <span className="ml-2 text-ink-muted font-normal normal-case tracking-normal">(shown on the journal index page)</span>
            </label>
            <textarea
              id="excerpt"
              data-testid="admin-post-excerpt-textarea"
              value={form.excerpt}
              onChange={onField("excerpt")}
              rows={3}
              className={`${INPUT_CLS} resize-y`}
            />
          </div>

          <div>
            <label className={LABEL_CLS}>Hero image (optional)</label>
            <div className="border border-rule bg-linen p-4 flex items-center gap-4">
              {form.hero_image_id ? (
                <img
                  src={`/api/media/${form.hero_image_id}`}
                  alt=""
                  className="w-32 h-24 object-cover"
                />
              ) : (
                <div className="w-32 h-24 bg-rule/40 flex items-center justify-center text-ink-muted">
                  <ImageIcon size={20} strokeWidth={1.5} />
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={heroInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={onHeroFile}
                  data-testid="admin-hero-upload-input"
                  className="text-sm font-accent"
                />
                {form.hero_image_id && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, hero_image_id: null }))}
                    className="ml-3 text-xs text-ink-muted hover:text-destructive underline"
                  >
                    Remove
                  </button>
                )}
                {uploading && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-ink-muted">
                    <Loader2 size={12} className="animate-spin" />
                    Uploading…
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="body" className={LABEL_CLS + " mb-0"}>Body (Markdown)</label>
              <label className="text-xs font-accent text-river hover:text-river-dark cursor-pointer underline underline-offset-4">
                Insert image
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={onInlineImage}
                  data-testid="admin-inline-image-input"
                  className="hidden"
                />
              </label>
            </div>
            <textarea
              id="body"
              ref={bodyInputRef}
              data-testid="admin-post-body-textarea"
              value={form.body}
              onChange={onField("body")}
              rows={18}
              className={`${INPUT_CLS} resize-y font-mono text-sm leading-relaxed`}
              placeholder={`Write in Markdown.\n\n## A subheading\n\nA paragraph. **Bold**, *italic*, [a link](https://example.com).\n\n> A quoted line.\n\n- A list item\n- Another item`}
            />
            <p className="mt-2 text-xs text-ink-muted">
              Use ## for headings, * or - for lists, &gt; for quotes, **bold**, [text](url) for links.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const PREVIEW_MARKDOWN_COMPONENTS = {
  h2: ({ node, ...p }) => <h2 className="font-serif text-2xl text-ink mt-8 mb-3" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-serif text-xl text-ink mt-6 mb-2" {...p} />,
  p: ({ node, ...p }) => <p className="my-4" {...p} />,
  a: ({ node, ...p }) => <a className="text-river underline" target="_blank" rel="noopener noreferrer" {...p} />,
  blockquote: ({ node, ...p }) => (
    <blockquote className="border-l-4 border-ember pl-6 my-6 font-serif text-xl text-ink italic" {...p} />
  ),
  img: ({ node, ...p }) => <img className="my-6 w-full h-auto" alt="" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-4 ml-6 list-disc space-y-1" {...p} />,
};

function PostPreview({ form }) {
  return (
    <div data-testid="admin-post-preview" className="border border-rule bg-white p-8 md:p-12 max-w-4xl">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        {form.tag || "News"}
      </div>
      <h1 className="font-serif text-3xl md:text-4xl text-ink mt-4 tracking-tight leading-tight">
        {form.title || "Untitled"}
      </h1>
      {form.hero_image_id && (
        <img src={`/api/media/${form.hero_image_id}`} alt="" className="mt-8 w-full h-auto" />
      )}
      <p className="mt-6 font-serif text-lg md:text-xl text-ink leading-snug">
        {form.excerpt || "(no excerpt)"}
      </p>
      <div className="mt-8 text-base text-ink-muted leading-[1.8] font-light prose-editorial">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={PREVIEW_MARKDOWN_COMPONENTS}
        >
          {form.body || "_(no body yet)_"}
        </ReactMarkdown>
      </div>
    </div>
  );
}
